import { Fragment, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Lightbulb, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { EmptyState } from '@/components/ui/empty-state';
import { Pagination } from '@/components/ui/pagination';
import { RangeSelectHint, SelectAllCheckbox } from '@/components/ui/range-select';
import { applyToSet, useRangeSelection } from '@/hooks/useRangeSelection';
import { useUrlState } from '@/hooks/useUrlState';
import { keepOnly } from '@/lib/rangeSelect';
import { useDiscoveryClient } from '../api/context';
import { ApiError, type SuggestionFilters } from '../api/types';
import { useFolders } from '../hooks/folders';
import { PLATFORMS, PLATFORM_LABELS } from '../lib/platform';
import { generationView, type GenerationView } from '../workspace/generationOutcome';
import { DebouncedInput, Field, Note, Segmented } from '../workspace/ui';
import { ApproveDialog, filtersInWords } from '../suggestions/ApproveDialog';
import { SuggestionCard } from '../suggestions/SuggestionCard';
import { SoundLink } from '../components/SoundLink';
import {
  useApproveSuggestion,
  useBulkApproveSuggestions,
  useBulkRejectSuggestions,
  useInvalidateSuggestions,
  useRejectSuggestion,
  useSuggestions,
} from '../suggestions/hooks';
import {
  CATEGORY_EMPTY,
  CATEGORY_LABELS,
  SORT_LABELS,
  SUGGESTION_CATEGORIES,
  SUGGESTION_SORTS,
  toCategory,
  toSort,
  type BulkResult,
  type SuggestedSearch,
  type SuggestionQuery,
} from '../suggestions/model';

const STATUSES = ['pending', 'approved', 'rejected'] as const;
type Status = (typeof STATUSES)[number];
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Waiting' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
] as const;

const URL_DEFAULTS = { category: '', status: 'pending', sort: 'expectedYield', dir: 'desc', platform: '', q: '', page: 1 };

const SEEN_RUN_KEY = 'discovery-suggestions-seen-run';

function readSeenRun(): string | null {
  try {
    return sessionStorage.getItem(SEEN_RUN_KEY);
  } catch {
    return null;
  }
}

function writeSeenRun(v: string | null): void {
  try {
    if (v) sessionStorage.setItem(SEEN_RUN_KEY, v);
    else sessionStorage.removeItem(SEEN_RUN_KEY);
  } catch {
    // Storage blocked: interruption detection still works for this page view.
  }
}

/**
 * Suggested searches: machine-proposed terms, grouped by where they came
 * from (campaigns, focus niches, creators who went on to post, folders that
 * converted, terms that are rising) and sortable by what a run is expected to
 * return.
 *
 * Generation is unattended and writes only pending rows — nothing here has
 * spent scrape budget yet. Approving opens a filter review and then runs the
 * search, which is why the action says so. Repeating is off unless asked for:
 * a bad term set to repeat burns API budget quietly until somebody notices.
 *
 * Every filter lives in the URL, so a tab or a sorted view can be linked.
 */
export function SuggestionsPanel(): JSX.Element {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  const [url, setUrl] = useUrlState(URL_DEFAULTS);
  const status: Status = (STATUSES as readonly string[]).includes(url.status) ? (url.status as Status) : 'pending';
  // A category from a stale or hand-edited link falls back to All.
  const category = url.category && toCategory(url.category) !== 'other' ? toCategory(url.category) : '';
  const query: SuggestionQuery = {
    category,
    status,
    sort: toSort(url.sort),
    dir: url.dir === 'asc' ? 'asc' : 'desc',
    platform: (PLATFORMS as readonly string[]).includes(url.platform) ? url.platform : '',
    q: url.q,
    page: Math.max(1, Math.floor(url.page) || 1),
  };
  const [approving, setApproving] = useState<{ suggestion: SuggestedSearch; schedule: boolean } | null>(null);

  const list = useSuggestions(query);
  const invalidate = useInvalidateSuggestions();
  const { data: folders } = useFolders({ includeArchived: true });
  const folderIdByName = useMemo(() => {
    // A name two folders share can't say which one is meant, so it isn't linked.
    const seen = new Map<string, string | null>();
    for (const f of folders ?? []) seen.set(f.name, seen.has(f.name) ? null : f.publicId);
    return new Map([...seen].filter((e): e is [string, string] => e[1] !== null));
  }, [folders]);

  // A pass takes minutes on the server, so the POST only starts one. Poll while
  // it runs and keep refetching the list: each source commits as it finishes.
  const generation = useQuery({
    queryKey: ['suggestion-generation'] as const,
    queryFn: () => client.workspace.generationStatus(),
    refetchInterval: (q) => {
      const d = q.state.data;
      return d && (d.outcome === 'running' || (!d.outcome && d.running)) ? 4000 : false;
    },
  });
  const run = generation.data ?? null;

  // Remember the run we saw in progress; if the server later restarts and
  // reports idle, that run died with the process (see generationView).
  const seenRunning = useRef<string | null>(readSeenRun());
  const running = !!run && (run.outcome === 'running' || (!run.outcome && run.running));
  if (running && run?.startedAt && seenRunning.current !== run.startedAt) {
    seenRunning.current = run.startedAt;
    writeSeenRun(run.startedAt);
  }
  const view = generationView(run, seenRunning.current);
  useEffect(() => {
    // A finished run (created / empty / failed) settles the question.
    if (view.kind === 'created' || view.kind === 'empty' || view.kind === 'failed') {
      seenRunning.current = null;
      writeSeenRun(null);
    }
  }, [view.kind]);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(invalidate, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const generate = useMutation({
    mutationFn: () => client.suggestions.generate(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suggestion-generation'] });
      invalidate();
    },
  });
  const defaults = useQuery({
    queryKey: ['suggestion-filter-defaults'] as const,
    queryFn: () => client.suggestions.filterDefaults(),
    staleTime: 30 * 60_000,
  });
  const approve = useApproveSuggestion();
  const reject = useRejectSuggestion();

  const data = list.data;
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const actionable = status === 'pending';

  // Bulk selection, in on-screen order. Only waiting suggestions can be acted on.
  const ids = useMemo(() => rows.map((r) => r.publicId), [rows]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const range = useRangeSelection({ visible: ids, isSelected: (id) => selected.has(id), onApply: applyToSet(setSelected) });
  const idsKey = ids.join(',');
  useEffect(() => {
    // A row that left the page (approved, filtered out, another tab) leaves the selection too.
    setSelected((prev) => keepOnly(prev, ids));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);
  const picked = actionable ? rows.filter((r) => selected.has(r.publicId)) : [];

  const filtered = !!query.q.trim() || !!query.platform;
  // When the last run came back empty, its banner already explains why; the
  // generic "nothing waiting" line would contradict it.
  const bannerExplainsEmpty =
    status === 'pending' && !category && !filtered && (view.kind === 'empty' || view.kind === 'failed' || view.kind === 'running');
  const unsupported = list.error instanceof ApiError && list.error.status === 404;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-bold tracking-[-0.03em]">Suggested searches</h1>
          <p className="mt-1 max-w-[75ch] text-sm text-muted-foreground">
            Terms worth searching, from live campaigns, focus niches and the creators and folders that went on to post.
            Nothing runs until you approve it.
          </p>
        </div>
        <Button onClick={() => generate.mutate()} disabled={generate.isPending || running}>
          {(generate.isPending || running) && <Loader2 className="h-4 w-4 animate-spin" />}
          {running ? 'Generating…' : 'Generate now'}
        </Button>
      </header>

      {generate.error ? (
        <Note tone="error" role="alert">
          Couldn't start generation: {(generate.error as Error).message}
        </Note>
      ) : (
        <GenerationBanner view={view} />
      )}

      <div role="tablist" aria-label="Suggestion category" className="flex flex-wrap gap-1 border-b border-border">
        {(['', ...SUGGESTION_CATEGORIES] as const).map((c) => {
          const n = data?.counts[c || 'all'] ?? null;
          return (
            <button
              key={c || 'all'}
              type="button"
              role="tab"
              aria-selected={category === c}
              data-category-tab={c || 'all'}
              onClick={() => setUrl({ category: c, page: 1 })}
              className={cn(
                '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-[120ms]',
                category === c ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {c ? CATEGORY_LABELS[c] : 'All'}
              {n != null && <span className="font-mono text-xs tabular-nums text-muted-foreground">{n.toLocaleString()}</span>}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Field label="Search" className="min-w-[12rem] flex-1 md:max-w-xs">
          <DebouncedInput
            type="search"
            value={url.q}
            placeholder="Term or niche"
            aria-label="Search suggestions"
            onCommit={(q) => setUrl({ q, page: 1 })}
          />
        </Field>
        <Field label="Platform">
          <Combobox
            className="w-full md:w-40"
            value={query.platform}
            onChange={(v) => setUrl({ platform: v, page: 1 })}
            allowAll
            allLabel="Any platform"
            aria-label="Platform"
            options={PLATFORMS.map((p) => ({ value: p, label: PLATFORM_LABELS[p] }))}
          />
        </Field>
        <Field label="Sort by">
          <div className="flex items-center gap-1">
            <Combobox
              className="w-full md:w-44"
              value={query.sort}
              onChange={(v) => setUrl({ sort: v || URL_DEFAULTS.sort, page: 1 })}
              aria-label="Sort by"
              options={SUGGESTION_SORTS.map((s) => ({ value: s, label: SORT_LABELS[s] }))}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => setUrl({ dir: query.dir === 'desc' ? 'asc' : 'desc', page: 1 })}
              aria-label={query.dir === 'desc' ? 'Highest first. Switch to lowest first' : 'Lowest first. Switch to highest first'}
              title={query.dir === 'desc' ? 'Highest first' : 'Lowest first'}
            >
              {query.dir === 'desc' ? <ArrowDownWideNarrow className="h-4 w-4" /> : <ArrowUpNarrowWide className="h-4 w-4" />}
            </Button>
          </div>
        </Field>
        <Field label="Show">
          <Segmented label="Suggestion status" value={status} onChange={(s) => setUrl({ status: s, page: 1 })} options={STATUS_OPTIONS} />
        </Field>
        {filtered && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setUrl({ q: '', platform: '', page: 1 })}>
            Clear filters
          </Button>
        )}
      </div>

      {data && rows.length > 0 && !data.hasFeedback && (
        <p className="text-sm text-muted-foreground" data-no-feedback>
          Expected yield and the creators each term is based on appear here once outreach results reach Discovery. Until
          then terms are ranked by score.
        </p>
      )}

      {unsupported && (
        <Note tone="warning" role="status">
          This page needs a Discovery service update that hasn't finished deploying.
        </Note>
      )}
      {list.error && !unsupported && (
        <Note tone="error" role="alert">
          Couldn't load suggestions: {(list.error as Error).message}
        </Note>
      )}

      {actionable && rows.length > 0 && (
        <BulkBar
          picked={picked}
          headState={range.headState}
          onToggleAll={range.toggleAll}
          onClear={() => setSelected(new Set())}
          defaults={defaults.data?.defaults ?? null}
          defaultsLoading={defaults.isLoading}
        />
      )}

      {list.isLoading ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
      ) : list.error ? null : rows.length === 0 ? (
        bannerExplainsEmpty ? null : (
          <div className="table-frame">
            <EmptyState
              icon={<Lightbulb className="h-6 w-6" strokeWidth={1.5} />}
              {...emptyCopy(status, category, filtered)}
            />
          </div>
        )
      ) : (
        <ul aria-label="Suggested searches" className={cn('space-y-2', list.isPlaceholderData && 'opacity-70')}>
          {rows.map((s) => (
            <SuggestionCard
              key={s.publicId}
              suggestion={s}
              selected={selected.has(s.publicId)}
              checkboxProps={actionable ? range.checkboxProps(s.publicId) : null}
              folderIdByName={folderIdByName}
              busy={approve.isPending || reject.isPending}
              onApprove={() => setApproving({ suggestion: s, schedule: false })}
              onSchedule={() => setApproving({ suggestion: s, schedule: true })}
              onReject={() =>
                reject.mutate(s.publicId, {
                  onError: (e) => toast.error(`Couldn't reject ${s.term}`, { description: (e as Error).message }),
                })
              }
            />
          ))}
        </ul>
      )}

      {data && data.pageSize != null && (
        <Pagination page={data.page} limit={data.pageSize} total={data.total} onPageChange={(page) => setUrl({ page })} />
      )}

      {/* Gated on defaults having settled: the dialog seeds its state once on
          open, so opening it mid-fetch would prefill an empty form and quietly
          run an UNFILTERED search — exactly what this dialog exists to stop. */}
      <ApproveDialog
        suggestion={approving && !defaults.isLoading ? approving.suggestion : null}
        schedule={approving?.schedule ?? false}
        defaults={defaults.data?.defaults ?? {}}
        busy={approve.isPending}
        error={approve.error as Error | null}
        onCancel={() => {
          setApproving(null);
          approve.reset();
        }}
        onConfirm={(input) =>
          approving &&
          approve.mutate(
            { id: approving.suggestion.publicId, input },
            {
              onSuccess: (res) => {
                setApproving(null);
                toast.success(
                  input.isRecurring
                    ? `Search running into "${res.folder.name}", repeating every ${input.recurEveryDays} day${input.recurEveryDays === 1 ? '' : 's'}`
                    : `Search running into "${res.folder.name}"`,
                );
              },
            },
          )
        }
      />
    </div>
  );
}

function emptyCopy(status: Status, category: string, filtered: boolean): { title: string; hint?: string } {
  if (filtered) return { title: 'No suggestions match these filters', hint: 'Clear the search or the platform filter.' };
  if (status !== 'pending') {
    const what = category ? `${CATEGORY_LABELS[toCategory(category)].toLowerCase()} suggestions` : 'suggestions';
    return { title: `No ${status} ${what}` };
  }
  return CATEGORY_EMPTY[category ? toCategory(category) : 'all'];
}

/**
 * Select-all plus bulk approve / reject. Both ask first, in place: approving
 * starts one search per suggestion, and that spends search budget.
 */
function BulkBar({
  picked,
  headState,
  onToggleAll,
  onClear,
  defaults,
  defaultsLoading,
}: {
  picked: SuggestedSearch[];
  headState: 'none' | 'some' | 'all';
  onToggleAll: () => void;
  onClear: () => void;
  /** The server's filter defaults; null until they load. */
  defaults: SuggestionFilters | null;
  defaultsLoading: boolean;
}): JSX.Element {
  const approve = useBulkApproveSuggestions();
  const reject = useBulkRejectSuggestions();
  const [confirm, setConfirm] = useState<'approve' | 'reject' | null>(null);
  const busy = approve.isPending || reject.isPending;
  const n = (count: number) => `${count} suggestion${count === 1 ? '' : 's'}`;
  useEffect(() => {
    if (picked.length === 0) setConfirm(null);
  }, [picked.length]);

  // A sound with no on-topic terms can't be topic-filtered, and the server
  // refuses it; those need the one-at-a-time review where terms can be added.
  const topicFiltered = defaults?.minTopicMatch != null;
  const needTerms = topicFiltered ? picked.filter((s) => s.searchMode === 'sound' && s.topicTerms.length === 0) : [];
  const approvable = picked.filter((s) => !needTerms.includes(s));
  const filterWords = defaults ? filtersInWords(defaults) : '';

  function finish(verb: string, toastId: string | number, result: BulkResult) {
    setConfirm(null);
    if (result.failed.length === 0) {
      toast.success(`${verb} ${n(result.done.length)}`, { id: toastId });
      onClear();
      return;
    }
    const terms = picked.filter((s) => result.failed.some((f) => f.id === s.publicId)).map((s) => s.term);
    toast.error(`${verb} ${n(result.done.length)}; ${result.failed.length} failed`, {
      id: toastId,
      description: `${terms.slice(0, 3).join(', ')}${terms.length > 3 ? ` and ${terms.length - 3} more` : ''} — ${result.failed[0]!.message}`,
    });
  }

  function run(action: 'approve' | 'reject') {
    const targets = action === 'approve' ? approvable : picked;
    if (targets.length === 0 || busy) return;
    const verbing = action === 'approve' ? 'Approving' : 'Rejecting';
    const done = action === 'approve' ? 'Approved' : 'Rejected';
    const id = toast.loading(`${verbing} ${n(targets.length)}…`);
    const onProgress = (i: number, total: number) => toast.loading(`${verbing} ${i} of ${n(total)}…`, { id });
    const ids = targets.map((s) => s.publicId);
    const handlers = {
      onSuccess: (result: BulkResult) => finish(done, id, result),
      onError: (e: unknown) => {
        setConfirm(null);
        toast.error(`Couldn't ${action} the suggestions`, { id, description: (e as Error).message });
      },
    };
    if (action === 'approve') approve.mutate({ ids, input: { filters: defaults ?? {} }, onProgress }, handlers);
    else reject.mutate({ ids, onProgress }, handlers);
  }

  return (
    <div className="space-y-2">
      <div
        role="group"
        aria-label="Suggestion selection"
        className={cn(
          'flex min-h-9 flex-wrap items-center gap-2 rounded-lg px-2 py-1',
          picked.length > 0 && 'border border-brand/30 bg-brand/10',
        )}
      >
        <label className="inline-flex cursor-pointer items-center gap-2 px-1 text-sm">
          <SelectAllCheckbox label="Select every suggestion shown" state={headState} onToggle={onToggleAll} />
          <span className={picked.length > 0 ? 'font-semibold' : 'text-muted-foreground'}>
            {picked.length > 0 ? `${n(picked.length)} selected` : 'Select all'}
          </span>
        </label>
        {picked.length > 0 && (
          <>
            <Button size="sm" disabled={busy || confirm !== null || defaultsLoading} onClick={() => setConfirm('approve')}>
              Approve {picked.length}
            </Button>
            <Button size="sm" variant="outline" disabled={busy || confirm !== null} onClick={() => setConfirm('reject')}>
              Reject {picked.length}
            </Button>
            <RangeSelectHint />
            <Button size="sm" variant="ghost" className="ml-auto" disabled={busy} onClick={onClear}>
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          </>
        )}
      </div>
      {confirm && picked.length > 0 && (
        <div
          role="alertdialog"
          aria-label={confirm === 'approve' ? `Approve ${n(picked.length)}?` : `Reject ${n(picked.length)}?`}
          className="flex flex-wrap items-center gap-3 rounded-lg border border-warning/35 bg-warning/10 px-3 py-2 text-sm"
        >
          <span className="min-w-0 flex-1">
            {confirm === 'approve' ? (
              <>
                Run {approvable.length === 1 ? 'this search' : `these ${approvable.length} searches`} now? Each one runs
                once into its own new folder with the default filters{filterWords ? ` (${filterWords})` : ''}, and spends
                search budget. Nothing repeats.
                {needTerms.length > 0 && (
                  <span className="mt-1 block text-muted-foreground">
                    {needTerms.length === 1 ? '1 sound has' : `${needTerms.length} sounds have`} no on-topic terms and will
                    be left waiting (
                    {needTerms.slice(0, 3).map((s, i) => (
                      <Fragment key={s.publicId}>
                        {i > 0 && ', '}
                        {s.musicId ? <SoundLink platform={s.platform} id={s.musicId} title={s.term} /> : s.term}
                      </Fragment>
                    ))}
                    {needTerms.length > 3 ? '…' : ''}). Approve {needTerms.length === 1 ? 'it' : 'those'} one at a time to add terms.
                  </span>
                )}
              </>
            ) : (
              <>Reject {n(picked.length)}? They move to Rejected and won't be suggested again soon.</>
            )}
          </span>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={busy || (confirm === 'approve' && approvable.length === 0)}
            onClick={() => run(confirm)}
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {confirm === 'approve' ? `Run ${approvable.length} search${approvable.length === 1 ? '' : 'es'}` : `Reject ${n(picked.length)}`}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * What the last (or current) generation pass did. A run takes minutes, so
 * "is it working?" has to be answerable while it's still going, and a run
 * that found nothing has to say why. Skipped sources stay listed in full: a
 * skipped source (say, an outreach key without the right scope) is why a
 * whole category is empty.
 */
function GenerationBanner({ view }: { view: GenerationView }): JSX.Element | null {
  if (view.kind === 'none') return null;
  return (
    <Note tone={view.tone} role={view.tone === 'error' ? 'alert' : 'status'}>
      <p>{view.message}</p>
      {view.progress && (
        <div className="mt-2 space-y-1">
          <div
            className="h-1.5 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={view.progress.total}
            aria-valuenow={view.progress.done}
            aria-label="Sources processed"
          >
            <div className="h-full rounded-full bg-brand transition-[width] duration-500" style={{ width: `${view.progress.pct}%` }} />
          </div>
          <p className="text-sm text-muted-foreground">
            {view.progress.done} of {view.progress.total} sources
            {view.progress.current ? ` · now: ${view.progress.current}` : ''}
          </p>
        </div>
      )}
      {view.skipped.length > 0 && (
        <details className="mt-2" open>
          <summary className="cursor-pointer text-sm text-muted-foreground">
            {view.skipped.length} source{view.skipped.length === 1 ? '' : 's'} skipped
          </summary>
          <ul className="mt-1 space-y-0.5 text-sm">
            {view.skipped.map((s) => (
              <li key={s.key}>
                <span className="font-medium">{s.label}</span>
                {s.reason && <span className="text-muted-foreground"> — {s.reason}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Note>
  );
}
